<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isReplyRestricted: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['toggleMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);

/**
 * Computed boolean indicating if the editor is in private note mode
 * When isReplyRestricted is true, force switch to private note
 * Otherwise, respect the current mode prop
 * @type {ComputedRef<boolean>}
 */
const isPrivate = computed(() => {
  if (props.isReplyRestricted) {
    // Force switch to private note when replies are restricted
    return true;
  }
  // Otherwise respect the current mode
  return props.mode === REPLY_EDITOR_MODES.NOTE;
});

/**
 * Computes the width of the sliding background chip in pixels
 * Includes 16px of padding in the calculation
 * @type {ComputedRef<string>}
 */
const width = computed(() => {
  const widthToUse = isPrivate.value
    ? privateModeSize.width.value
    : replyModeSize.width.value;

  const widthWithPadding = widthToUse + 16;
  return `${widthWithPadding}px`;
});

/**
 * Computes the X translation value for the sliding background chip
 * Translates by the width of reply mode + padding when in private mode
 * @type {ComputedRef<string>}
 */
const translateValue = computed(() => {
  const xTranslate = isPrivate.value ? replyModeSize.width.value + 16 : 0;

  return `${xTranslate}px`;
});
</script>

<template>
  <button
    type="button"
    class="sd-mode-toggle flex items-center w-auto h-8 p-1 group relative z-0"
    :disabled="disabled || isReplyRestricted"
    :aria-label="$t('CONVERSATION.REPLYBOX.MODE_LABEL')"
    :title="
      isReplyRestricted ? $t('CONVERSATION.REPLYBOX.REPLY_LOCKED') : undefined
    "
    :class="{
      'cursor-not-allowed': disabled || isReplyRestricted,
    }"
    @click="$emit('toggleMode')"
  >
    <div
      ref="wootEditorReplyMode"
      class="sd-mode flex items-center gap-1 px-2 z-20"
      :class="{
        'sd-mode--active': !isPrivate,
        'sd-mode--locked': isReplyRestricted,
      }"
      :aria-disabled="isReplyRestricted"
    >
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
      <span
        v-if="isReplyRestricted"
        class="i-lucide-lock size-3"
        aria-hidden="true"
      />
    </div>
    <div
      ref="wootEditorPrivateMode"
      class="sd-mode flex items-center gap-1 px-2 z-20"
      :class="{ 'sd-mode--active': isPrivate }"
    >
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </div>
    <div
      class="sd-mode-chip absolute h-6 w-[var(--chip-width)] translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)]"
      :class="{
        'transition-transform duration-150 ease-out':
          !disabled && !isReplyRestricted,
      }"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </button>
</template>
